import { ChatPanel } from "llmasaservice-ui";
import { useUser } from "../contexts/useUser";
import "../../node_modules/llmasaservice-ui/dist/index.css";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import { fetchLLMModels } from "../dataaccess/fetchLLMModels";
import { useState } from "react";
import { Checkbox } from "@mui/material";
import { LLMEmailCaptureMode } from "../API";
const ChatTest: React.FC = () => {
  const { project } = useUser();
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [createConversation, setCreateConversation] = useState<boolean>(false);
  const [customerEmailCaptureMode, setCustomerEmailCaptureMode] =
    useState<LLMEmailCaptureMode>(LLMEmailCaptureMode.HIDE);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const { data: modelsData, isLoading: modelsLoading, isError: modelsError } = useQuery({
    queryKey: ["models"],
    queryFn: () => fetchLLMModels(),
    enabled: true,
  });
  if (isLoading || modelsLoading) {
    return <div>Loading...</div>;
  }

  if (isError || modelsError) {
    return <div>Error...</div>;
  }

  const groups = data?.data?.groupsByProjectId?.items ?? [];
  const models = modelsData?.data?.listLLMModels?.items ?? [];

  let publicAPIUrl = "https://api.llmasaservice.io";

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
    publicAPIUrl = "https://8ftw8droff.execute-api.us-east-1.amazonaws.com/dev";
  }
  const THEME_KEY = "_llmasaservice_theme";
  const userThemePreference = localStorage.getItem(THEME_KEY) || "dark";

  // Group models by their default service group and vendor
  const modelsByServiceGroup = models.reduce((acc, model) => {
    const serviceGroup = model?.defaultServiceGroup || "Other";
    if (!acc[serviceGroup]) {
      acc[serviceGroup] = [];
    }
    acc[serviceGroup].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const modelsByVendor = models.reduce((acc, model) => {
    const vendor = model?.vendor || "Unknown";
    if (!acc[vendor]) {
      acc[vendor] = [];
    }
    acc[vendor].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const options = [
    { id: "", label: "Default group", type: "default" },
    
    // Service Groups
    { id: "group:standard", label: "Standard Model Group", type: "service-group" },
    { id: "group:premium", label: "Premium Model Group", type: "service-group" },
    { id: "group:reasoning", label: "Reasoning Model Group", type: "service-group" },
    { id: "group:superpremium", label: "Super Premium Model Group", type: "service-group" },
    { id: "group:supersmall", label: "Super Small Model Group", type: "service-group" },

    // Custom Groups
    ...(groups?.map((group) => ({
      id: group?.id ?? "",
      label: "Custom group: " + (group?.name ?? ""),
      type: "custom-group"
    })) ?? []),
    
    // Separator for Models by Service Group
    { id: "separator-service", label: "— Models by Service Group —", type: "separator", disabled: true },
    
    // Models grouped by Service Group
    ...Object.entries(modelsByServiceGroup)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([serviceGroup, groupModels]) => [
        // Service group header
        { 
          id: `header-service-${serviceGroup}`, 
          label: `${serviceGroup} Models`, 
          type: "header", 
          disabled: true 
        },        // Models in this service group
        ...groupModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name} (${model?.vendor}${model?.host && model?.host !== model?.vendor ? ` via ${model?.host}` : ""})`,
            type: "model"
          }))
      ]),
    
    // Separator for Models by Vendor
    { id: "separator-vendor", label: "— Models by Vendor —", type: "separator", disabled: true },
    
    // Models grouped by Vendor
    ...Object.entries(modelsByVendor)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([vendor, vendorModels]) => [
        // Vendor header
        { 
          id: `header-vendor-${vendor}`, 
          label: `${vendor} Models`, 
          type: "header", 
          disabled: true 
        },        // Models from this vendor
        ...vendorModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name}${model?.host && model?.host !== model?.vendor ? ` (via ${model?.host})` : ""}`,
            type: "model"
          }))
      ])
  ];

  return (
    <Box margin={2}>
      <Paper
        elevation={3}
        style={{ borderRadius: 3, border: "1px solid #444" }}
      >
        <Box padding={2} margin={1}>
          <FormControl>
            <InputLabel id="group-select-label">Service Group</InputLabel>            <Select
              labelId="group-select-label"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              label="Service Group"
              style={{ width: "400px" }}
              size="small">
              {options.map((option) => (
                <MenuItem 
                  key={option.id} 
                  value={option.id}
                  disabled={option.disabled}                  style={{
                    fontWeight: option.type === "header" ? "bold" : "normal",
                    fontSize: option.type === "header" ? "0.9em" : option.type === "separator" ? "0.85em" : "1em",
                    color: option.type === "separator" || option.type === "header" ? "#666" : "inherit",
                    backgroundColor: option.type === "separator" || option.type === "header" ? "#f5f5f5" : "inherit",
                    paddingLeft: option.type === "model" ? "32px" : "16px",
                    fontStyle: option.type === "separator" ? "italic" : "normal",
                    cursor: option.disabled ? "default" : "pointer"
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ ml: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createConversation}
                  onChange={(e) => setCreateConversation(e.target.checked)}
                />
              }
              label="Save conversation?"
            />
          </FormControl>

          <FormControl>
            <InputLabel id="email-capture-label">Email capture mode</InputLabel>
            <Select
              labelId="email-capture-label"
              value={customerEmailCaptureMode}
              onChange={(e: any) => setCustomerEmailCaptureMode(e.target.value)}
              label="Email capture mode"
              style={{ width: "300px" }}
              size="small"
            >
              <MenuItem value={LLMEmailCaptureMode.HIDE}>Hide</MenuItem>
              <MenuItem value={LLMEmailCaptureMode.OPTIONAL}>Optional</MenuItem>
              <MenuItem value={LLMEmailCaptureMode.REQUIRED}>Required</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <ChatPanel
        project_id={project?.id ?? ""}
        initialMessage={`Hello, use this chat to test the LLM services defined in this project. Tokens are used from the customer called ${project?.name}.`}
        theme={userThemePreference === "light" ? "light" : "dark"}
        height="80vh"
        width="100%"
        url={url}
        hideInitialPrompt={false}
        service={selectedGroup}
        createConversationOnFirstChat={createConversation}
        customerEmailCaptureMode={customerEmailCaptureMode}
      />
    </Box>
  );
};

export default ChatTest;
